import { unwrapResult } from '@reduxjs/toolkit';
import { useAppSelector } from 'hooks';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { ProjectResponse } from 'types/entity/project-response';
import { PositionTypeQuery, ProjectAndAdditionalInfoResponse } from 'types/entity';
import { enumFromString } from 'types/support/enum-from-string';
import { projectReadQuery } from './project-read.selector';
import { projectReadActions } from './project-read.slice';

export const useProjectReadFacade = () => {
  const dispatch: AppDispatch = useDispatch();

  /** ロード済みか */
  const loaded = useAppSelector(projectReadQuery.getLoaded);
  /** プロジェクト一覧 */
  const projects: ProjectResponse[] = useAppSelector(projectReadQuery.getEntities);
  /** プロジェクト一覧ロード済みか */
  const projectListLoaded = useAppSelector(projectReadQuery.getProjectListLoaded);
  /** 選択中のプロジェクト */
  const selectedProject: ProjectAndAdditionalInfoResponse = useAppSelector(projectReadQuery.getSelectedEntity);
  /** 選択中のプロジェクトID */
  const selectedProjectId = useAppSelector(projectReadQuery.getSelectedProjectId);
  /** 選択中のプロジェクトはテンプレートプロジェクトか */
  const isTemplateProject: boolean = useAppSelector(projectReadQuery.getSelectedProjectTemplateFrag);
  /** 選択中のプロジェクトで管理者か */
  const isProjectAdmin: boolean = useAppSelector(projectReadQuery.getSelectedProjectAdminFrag);
  /** 選択中のプロジェクトがファイル管理ポータルが読込可能なプロジェクトか */
  const isFpReadableProject: boolean = useAppSelector(projectReadQuery.isFpReadableProject);
  /** 選択中のプロジェクトがファイル管理ポータルが編集可能なプロジェクトか */
  const isFpEditableProject: boolean = useAppSelector(projectReadQuery.isFpEditableProject);
  /** 選択中のプロジェクトが拡張属性が編集可能なプロジェクトか */
  const isEditableExtendedAttributeProject: boolean = useAppSelector(
    projectReadQuery.isEditableExtendedAttributeProject,
  );
  /** 選択中のプロジェクトが図面書類管理が読込可能なプロジェクトか */
  const isDdmsReadableProject: boolean = useAppSelector(projectReadQuery.isDdmsReadableProject);
  /** 選択中のプロジェクトが写真が読込可能なプロジェクトか */
  const isPhotoReadableProject: boolean = useAppSelector(projectReadQuery.isPhotoReadableProject);
  /** 選択中のプロジェクトが属するサービスの一覧 */
  const serviceProjectPolicies = useAppSelector(projectReadQuery.getServiceProjectPolicies);

  const loadDefaultProject = useCallback(
    () => dispatch(projectReadActions.loadDefaultProject()).then(unwrapResult),
    [dispatch],
  );

  const loadProject = useCallback(
    (projectId: string) => dispatch(projectReadActions.loadProject({ projectId })).then(unwrapResult),
    [dispatch],
  );

  const loadProjectList = useCallback(
    () => dispatch(projectReadActions.loadProjectList()).then(unwrapResult),
    [dispatch],
  );

  const selectProject = useCallback(
    (projectId: string) => dispatch(projectReadActions.selectOne(projectId)),
    [dispatch],
  );

  /** 遷移元指定 */
  const setPositionType = useCallback(
    (positionType: string | null) => {
      const positionTypeQuery: PositionTypeQuery | undefined = enumFromString(PositionTypeQuery, positionType || '');
      return dispatch(projectReadActions.setPositionType(positionTypeQuery));
    },
    [dispatch],
  );

  return {
    loaded,
    projectListLoaded,
    projects,
    selectedProject,
    selectedProjectId,
    isTemplateProject,
    isProjectAdmin,
    isFpReadableProject,
    isFpEditableProject,
    isDdmsReadableProject,
    isPhotoReadableProject,
    isEditableExtendedAttributeProject,
    serviceProjectPolicies,
    loadDefaultProject,
    loadProject,
    loadProjectList,
    selectProject,
    setPositionType,
  };
};
